import { ReactNode } from 'react'

import confetti from 'canvas-confetti'
import { create } from 'zustand'

import { ButtonProps } from 'components/atomic/atoms/button'

type ToastTypes = 'error' | 'info' | 'success'

type ToastContent = {
  title: string
  text?: ReactNode
  button?: ButtonProps
}

export type Toast = {
  id: string
  content: ToastContent
  type: ToastTypes
  duration?: number
}

interface ToastsState {
  items: Toast[]
  add: (toast: Toast) => void
  remove: (id: string) => void
}

const useToastsStore = create<ToastsState>((set, get) => ({
  items: [],
  add: toast => {
    if (toast.duration) {
      setTimeout(() => {
        get().remove(toast.id)
      }, toast.duration)
    }

    set({ items: [...get().items, toast] })
  },
  remove: id => {
    set({ items: get().items.filter(t => t.id !== id) })
  },
}))

export const useGetToasts = () => useToastsStore(s => s.items)

const dispatchToast = ({
  content,
  type,
  duration,
}: {
  content: ToastContent
  type: ToastTypes
  duration: number
}) => {
  const id = crypto.randomUUID()

  useToastsStore.getState().add({
    id,
    content,
    type,
    duration,
  })
}

export const toasty = {
  info: (content: ToastContent) =>
    dispatchToast({
      content,
      type: 'info',
      duration: 5000,
    }),
  success: (content: ToastContent) =>
    dispatchToast({
      content,
      type: 'success',
      duration: 5000,
    }),
  error: (content: ToastContent) =>
    dispatchToast({
      content,
      type: 'error',
      duration: 8000,
    }),
  confetti: (content: ToastContent, type: ToastTypes = 'info') => {
    confetti({
      particleCount: 360,
      spread: 180,
      angle: 60,
      origin: { x: 1, y: 1 },
      disableForReducedMotion: true,
    })
    dispatchToast({
      content,
      type,
      duration: 7000,
    })
  },
}

export const toastError = () =>
  toasty.error({
    title: 'Internal error  💥',
    text: 'Try again or contact support.',
  })

export const removeToast = (id: string) => useToastsStore.getState().remove(id)
